'use client';

import { c as _c } from "react/compiler-runtime";
import { registerBeforeExecuteListener } from '@data-access/lib/execute';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createContext, type ReactNode, startTransition, useContext, useEffect, useEffectEvent, useRef, useState } from 'react';
import { shouldRefreshServerShellForSession, shouldResetAppSessionCache } from './app-session.logic';
import { APP_SESSION_QUERY_KEY, accessMarkerCookieExists, fetchAppSession, refreshMarkerCookieExists, shouldSkipAuthPreflight } from './app-session.shared';
import type { AppSessionUser } from './app-session.types';
type AppSessionContextValue = {
  user: AppSessionUser | null;
  loading: boolean;
  checkAuth: () => Promise<AppSessionUser | null>;
  refresh: () => Promise<AppSessionUser | null>;
};
const appSessionContext = createContext<AppSessionContextValue | null>(null);
export function AppSessionProvider(t0) {
  const $ = _c(47);
  const {
    children,
    initialUser
  } = t0;
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const router = useRouter();
  const search = useSearchParams()?.toString() ?? "";
  const refreshPromiseRef = useRef(null);
  const refreshQueuedRef = useRef(false);
  const resolvedSessionRef = useRef(initialUser);
  const pendingShellRefreshRef = useRef(false);
  const lastPathnameRef = useRef(null);
  const [authHintPresent, setAuthHintPresent] = useState(Boolean(initialUser));
  const [bootstrapEnabled, setBootstrapEnabled] = useState(false);
  const [bootstrapNeedsShellRefresh, setBootstrapNeedsShellRefresh] = useState(false);
  const [sessionResolved, setSessionResolved] = useState(typeof initialUser !== "undefined");
  const navigationKey = `${pathname}?${search}`;
  let t1;
  if ($[0] !== bootstrapEnabled || $[1] !== initialUser) {
    t1 = {
      queryKey: APP_SESSION_QUERY_KEY,
      queryFn: fetchAppSession,
      enabled: bootstrapEnabled,
      initialData: initialUser,
      retry: false,
      staleTime: 0
    };
    $[0] = bootstrapEnabled;
    $[1] = initialUser;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    error,
    isError,
    isFetching
  } = useQuery(t1);
  let t2;
  if ($[3] !== router) {
    t2 = () => {
      if (refreshQueuedRef.current) {
        return;
      }
      refreshQueuedRef.current = true;
      queueMicrotask(() => {
        refreshQueuedRef.current = false;
        startTransition(() => {
          router.refresh();
        });
      });
    };
    $[3] = router;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const queueServerRefresh = useEffectEvent(t2);
  let t3;
  if ($[5] !== queryClient || $[6] !== queueServerRefresh) {
    t3 = (session, t4) => {
      const {
        syncServerShell: t5
      } = t4 === undefined ? {} : t4;
      const syncServerShell = t5 === undefined ? false : t5;
      const previousSession = resolvedSessionRef.current;
      const shouldResetCache = shouldResetAppSessionCache(previousSession, session);
      if (shouldResetCache) {
        queryClient.clear();
      }
      queryClient.setQueryData(APP_SESSION_QUERY_KEY, session);
      resolvedSessionRef.current = session;
      pendingShellRefreshRef.current = false;
      setBootstrapEnabled(false);
      setBootstrapNeedsShellRefresh(false);
      setSessionResolved(true);
      if (syncServerShell) {
        queueServerRefresh();
      }
    };
    $[5] = queryClient;
    $[6] = queueServerRefresh;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const applyResolvedSession = useEffectEvent(t3);
  let t4;
  if ($[8] !== applyResolvedSession || $[9] !== pathname) {
    t4 = _navigationKey => {
      const serverRendered = lastPathnameRef.current !== pathname;
      lastPathnameRef.current = pathname;
      const accessMarkerPresent = accessMarkerCookieExists();
      const refreshMarkerPresent = refreshMarkerCookieExists();
      setAuthHintPresent(accessMarkerPresent || refreshMarkerPresent);
      if (!accessMarkerPresent && !refreshMarkerPresent) {
        applyResolvedSession(null, {
          syncServerShell: resolvedSessionRef.current !== undefined && resolvedSessionRef.current !== null
        });
        return;
      }
      if (accessMarkerPresent && resolvedSessionRef.current && !pendingShellRefreshRef.current) {
        setBootstrapEnabled(false);
        setBootstrapNeedsShellRefresh(false);
        setSessionResolved(true);
        return;
      }
      if (serverRendered && !accessMarkerPresent && refreshMarkerPresent) {
        pendingShellRefreshRef.current = true;
      }
      setBootstrapEnabled(true);
      setBootstrapNeedsShellRefresh(pendingShellRefreshRef.current);
      setSessionResolved(false);
    };
    $[8] = applyResolvedSession;
    $[9] = pathname;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const syncFromMarkerCookies = useEffectEvent(t4);
  let t5;
  if ($[11] !== applyResolvedSession || $[12] !== queryClient) {
    t5 = async t6 => {
      const {
        refreshServerShell: t7
      } = t6 === undefined ? {} : t6;
      const refreshServerShell = t7 === undefined ? false : t7;
      if (!accessMarkerCookieExists() && !refreshMarkerCookieExists()) {
        setAuthHintPresent(false);
        applyResolvedSession(null, {
          syncServerShell: refreshServerShell
        });
        return null;
      }
      setAuthHintPresent(true);
      setBootstrapEnabled(true);
      setSessionResolved(false);
      if (refreshServerShell) {
        pendingShellRefreshRef.current = true;
      }
      if (refreshPromiseRef.current) {
        return refreshPromiseRef.current;
      }
      refreshPromiseRef.current = queryClient.fetchQuery({
        queryKey: APP_SESSION_QUERY_KEY,
        queryFn: fetchAppSession,
        retry: false,
        staleTime: 0
      }).then(session_0 => {
        const previousSession_0 = resolvedSessionRef.current;
        const syncServerShellOnResolution = shouldRefreshServerShellForSession(previousSession_0, session_0, {
          forceAuthenticatedRefresh: pendingShellRefreshRef.current
        });
        applyResolvedSession(session_0, {
          syncServerShell: syncServerShellOnResolution
        });
        return session_0;
      }).catch(() => {
        setBootstrapEnabled(false);
        setBootstrapNeedsShellRefresh(false);
        setSessionResolved(typeof resolvedSessionRef.current !== "undefined");
        return resolvedSessionRef.current ?? null;
      }).finally(() => {
        refreshPromiseRef.current = null;
      });
      return refreshPromiseRef.current;
    };
    $[11] = applyResolvedSession;
    $[12] = queryClient;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const resolveSession = useEffectEvent(t5);
  let t6;
  if ($[14] !== resolveSession) {
    t6 = () => resolveSession({
      refreshServerShell: !accessMarkerCookieExists() && refreshMarkerCookieExists()
    });
    $[14] = resolveSession;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const resolveSessionFromMarkers = useEffectEvent(t6);
  let t7;
  if ($[16] !== navigationKey || $[17] !== syncFromMarkerCookies) {
    t7 = () => {
      syncFromMarkerCookies(navigationKey);
    };
    $[16] = navigationKey;
    $[17] = syncFromMarkerCookies;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== navigationKey) {
    t8 = [navigationKey];
    $[19] = navigationKey;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  useEffect(t7, t8);
  let t9;
  if ($[21] !== applyResolvedSession || $[22] !== bootstrapEnabled || $[23] !== bootstrapNeedsShellRefresh || $[24] !== data || $[25] !== isError || $[26] !== isFetching) {
    t9 = () => {
      if (!bootstrapEnabled || isFetching || isError || typeof data === "undefined") {
        return;
      }
      const previousSession_1 = resolvedSessionRef.current;
      const nextSession = data ?? null;
      const syncServerShell_0 = shouldRefreshServerShellForSession(previousSession_1, nextSession, {
        forceAuthenticatedRefresh: bootstrapNeedsShellRefresh
      });
      applyResolvedSession(nextSession, {
        syncServerShell: syncServerShell_0
      });
    };
    $[21] = applyResolvedSession;
    $[22] = bootstrapEnabled;
    $[23] = bootstrapNeedsShellRefresh;
    $[24] = data;
    $[25] = isError;
    $[26] = isFetching;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== bootstrapEnabled || $[29] !== bootstrapNeedsShellRefresh || $[30] !== data || $[31] !== isError || $[32] !== isFetching) {
    t10 = [bootstrapEnabled, bootstrapNeedsShellRefresh, data, isError, isFetching];
    $[28] = bootstrapEnabled;
    $[29] = bootstrapNeedsShellRefresh;
    $[30] = data;
    $[31] = isError;
    $[32] = isFetching;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[34] !== error) {
    t11 = () => {
      if (!error) {
        return;
      }
      setBootstrapEnabled(false);
      setBootstrapNeedsShellRefresh(false);
      setSessionResolved(typeof resolvedSessionRef.current !== "undefined");
    };
    t12 = [error];
    $[34] = error;
    $[35] = t11;
    $[36] = t12;
  } else {
    t11 = $[35];
    t12 = $[36];
  }
  useEffect(t11, t12);
  let t13;
  if ($[37] !== resolveSession) {
    t13 = () => {
      registerBeforeExecuteListener(async query => {
        if (shouldSkipAuthPreflight(query)) {
          return;
        }
        if (accessMarkerCookieExists() || !refreshMarkerCookieExists()) {
          return;
        }
        await resolveSession();
      });
      return _temp;
    };
    $[37] = resolveSession;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  useEffect(t13, t14);
  const t15 = sessionResolved ? data ?? null : null;
  const t16 = authHintPresent && !sessionResolved;
  let t17;
  if ($[40] !== resolveSessionFromMarkers || $[41] !== t15 || $[42] !== t16) {
    t17 = {
      user: t15,
      loading: t16,
      checkAuth: resolveSessionFromMarkers,
      refresh: resolveSessionFromMarkers
    };
    $[40] = resolveSessionFromMarkers;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const value = t17;
  let t18;
  if ($[44] !== children || $[45] !== value) {
    t18 = <appSessionContext.Provider value={value}>{children}</appSessionContext.Provider>;
    $[44] = children;
    $[45] = value;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
}
function _temp() {
  registerBeforeExecuteListener(null);
}
export function useAppSession() {
  const context = useContext(appSessionContext);
  if (!context) {
    throw new Error("useAppSession must be used within AppSessionProvider");
  }
  return context;
}